import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Button from '../components/ui/Button';
import { db } from '../lib/db';
import { auth } from '../lib/auth';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { FaFacebook, FaInstagram, FaWhatsapp, FaLinkedin, FaGlobe, FaLayerGroup } from 'react-icons/fa';

const { FiPlus, FiTrash2, FiExternalLink, FiGrid, FiList, FiCopy, FiCheck, FiLoader, FiSettings, FiX, FiEye, FiDownload, FiMessageSquare, FiLogOut } = FiIcons;

const AdminPanel = () => {
  const navigate = useNavigate();
  const [tags, setTags] = useState([]);
  const [view, setView] = useState('grid');
  const [copiedId, setCopiedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [error, setError] = useState(null);
  const [customizingId, setCustomizingId] = useState(null);
  const [qrStyles, setQrStyles] = useState({});

  const currentUser = auth.getCurrentUser();

  const BRAND_ICONS = [
    { id: 'facebook', icon: FaFacebook, color: '#1877F2', label: 'FB' },
    { id: 'instagram', icon: FaInstagram, color: '#E4405F', label: 'IG' },
    { id: 'whatsapp', icon: FaWhatsapp, color: '#25D366', label: 'WA' },
    { id: 'google', icon: 'https://i.postimg.cc/Zn4tZrjm/images-removebg-preview.png', color: '#ffffff', label: 'Google', isImage: true },
    { id: 'linkedin', icon: FaLinkedin, color: '#0A66C2', label: 'In' },
    { id: 'web', icon: FaGlobe, color: '#111827', label: 'Web' },
    { id: 'smart', icon: FaLayerGroup, color: '#DC2626', label: 'Smart' },
  ];

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    setLoading(true);
    setError(null);
    const data = await db.getAll();
    setTags(data);
    setLoading(false);
  };

  const handleCreate = async () => {
    setLoading(true);
    setError(null);
    const result = await db.create();
    if (!result) {
      setError("Failed to create tag. Check connection.");
    }
    await loadTags();
  };

  const handleDelete = async (id) => {
    if (!id) return;
    if (window.confirm('Are you sure you want to delete this tag? This action cannot be undone.')) {
      setDeletingId(id);
      setError(null);
      const success = await db.delete(id);
      if (success) {
        await loadTags();
      } else {
        alert("Failed to delete tag. Please try again.");
      }
      setDeletingId(null);
    }
  };

  const handleDownloadQR = async (tag, domId) => {
    setDownloadingId(tag.id);
    const element = document.getElementById(domId);
    if (!element) {
      setDownloadingId(null);
      return;
    }
    try {
      const canvas = await html2canvas(element, {
        backgroundColor: '#ffffff',
        scale: 4,
        logging: false,
        useCORS: true,
        allowTaint: true,
      });
      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `connectkard-${tag.tag_code}.png`;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("QR Download failed:", err);
      alert("Failed to download QR code.");
    } finally {
      setDownloadingId(null);
    }
  };

  const getScanUrl = (tagCode) => {
    const baseUrl = window.location.origin + window.location.pathname;
    const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    return `${cleanBase}/#/tag?tag_id=${tagCode}`;
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const setStyleForCard = (id, styleId) => {
    setQrStyles(prev => ({ ...prev, [id]: styleId }));
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      auth.logout();
      navigate('/admin/login');
    }
  };

  const RenderQR = ({ value, size = 100, styleId, domId }) => {
    const activeBrand = BRAND_ICONS.find(b => b.id === styleId);
    const Icon = activeBrand?.icon;
    const iconColor = activeBrand?.color;
    const isImage = activeBrand?.isImage;

    return (
      <div id={domId} className="relative flex justify-center items-center bg-white p-2 rounded-lg" style={{ width: size, height: size }}>
        <QRCodeSVG value={value} size={size - 16} level="H" bgColor="#ffffff" fgColor="#000000" />
        {activeBrand && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-white p-1 rounded-full shadow-sm border border-white flex items-center justify-center">
              {isImage ? (
                <img src={Icon} alt="brand" style={{ width: size * 0.2, height: size * 0.2, objectFit: 'contain' }} crossOrigin="anonymous" />
              ) : (
                <Icon size={size * 0.2} color={iconColor} />
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading && tags.length === 0) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[50vh] space-y-4">
          <div className="w-16 h-16 border-4 border-zinc-800 border-t-red-600 rounded-full animate-spin" />
          <p className="text-zinc-500 animate-pulse">Loading tags...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 pb-20">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Admin Dashboard</h1>
            <p className="text-zinc-500 text-sm">
              Welcome, <span className="text-white font-medium">{currentUser?.full_name || currentUser?.username}</span>
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/admin/reviews">
              <Button variant="secondary" className="w-auto py-2 px-3 text-sm border-zinc-700 bg-zinc-900">
                <SafeIcon icon={FiMessageSquare} /> Negative Reviews
              </Button>
            </Link>
            <Button onClick={loadTags} variant="secondary" className="w-auto py-2 px-3 text-sm">
              <SafeIcon icon={FiExternalLink} className="rotate-180" /> Refresh
            </Button>
            <Button onClick={handleCreate} className="w-auto py-2 px-4 text-sm" disabled={loading}>
              <SafeIcon icon={FiPlus} /> Generate Tag
            </Button>
            <Button onClick={handleLogout} variant="outline" className="w-auto py-2 px-3 text-sm border-red-500/30 text-red-500 hover:bg-red-500/10">
              <SafeIcon icon={FiLogOut} />
            </Button>
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex justify-end">
          <div className="flex gap-1 p-1 bg-zinc-950 rounded-lg border border-zinc-800 shrink-0">
            <button
              onClick={() => setView('grid')}
              className={`p-2 rounded-md transition-colors ${view === 'grid' ? 'bg-zinc-800 text-white shadow-sm' : 'text-zinc-500 hover:text-zinc-300'}`}
            >
              <SafeIcon icon={FiGrid} />
            </button>
            <button
              onClick={() => setView('list')}
              className={`p-2 rounded-md transition-colors ${view === 'list' ? 'bg-zinc-800 text-white shadow-sm' : 'text-zinc-500 hover:text-zinc-300'}`}
            >
              <SafeIcon icon={FiList} />
            </button>
          </div>
        </div>

        {tags.length === 0 && !loading ? (
          <div className="text-center py-20 border-2 border-dashed border-zinc-800 rounded-3xl bg-zinc-900/20">
            <p className="text-zinc-500 mb-4">No tags generated yet.</p>
            <Button onClick={handleCreate} variant="secondary" className="w-auto mx-auto">
              Create your first tag
            </Button>
          </div>
        ) : (
          <div className={view === 'grid' ? "grid grid-cols-1 gap-6" : "space-y-4"}>
            {tags.map((tag) => {
              // Standard Web URL
              let scanUrl = getScanUrl(tag.tag_code);
              
              const isDeleting = deletingId === tag.id;
              const isCustomizing = customizingId === tag.id;
              const isDownloading = downloadingId === tag.id;
              const currentStyle = qrStyles[tag.id];
              const isActive = tag.is_active === 1;
              const qrDomId = `qr-code-${tag.id}`;

              return (
                <div
                  key={tag.id || Math.random()}
                  className={`bg-zinc-900/80 border border-zinc-800 rounded-2xl overflow-hidden group hover:border-zinc-700 transition-all ${isDeleting ? 'opacity-50 pointer-events-none grayscale' : ''} ${isCustomizing ? 'ring-2 ring-red-500/50 border-red-500/50' : ''}`}
                >
                  <div className="p-5 flex gap-5">
                    {/* QR Code Section */}
                    <div className="flex flex-col gap-3 items-center">
                      <div className="bg-white p-1 rounded-xl shrink-0 h-fit shadow-lg shadow-black/20 relative overflow-hidden">
                        <RenderQR value={scanUrl} size={110} styleId={currentStyle} domId={qrDomId} />
                        {(isDeleting || isDownloading) && (
                          <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-xl z-20">
                            <SafeIcon icon={FiLoader} className="text-zinc-800 animate-spin text-2xl" />
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1 w-full">
                        <button
                          onClick={() => setCustomizingId(isCustomizing ? null : tag.id)}
                          className={`flex-1 text-[10px] font-bold flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg border transition-all ${isCustomizing ? 'bg-red-500 text-white border-red-500' : 'text-zinc-500 border-zinc-700 hover:border-zinc-500 hover:text-white bg-zinc-800'}`}
                          title="Style QR Code"
                        >
                          <SafeIcon icon={FiSettings} /> {isCustomizing ? 'Done' : 'Style'}
                        </button>
                        <button
                          onClick={() => handleDownloadQR(tag, qrDomId)}
                          disabled={isDownloading}
                          className="text-[10px] font-bold flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg border border-zinc-700 text-zinc-500 hover:border-zinc-500 hover:text-white bg-zinc-800 transition-all"
                          title="Download QR PNG"
                        >
                          <SafeIcon icon={FiDownload} />
                        </button>
                      </div>
                    </div>

                    {/* Info Section */}
                    <div className="flex-1 min-w-0 flex flex-col justify-between py-1">
                      <div>
                        {isCustomizing ? (
                          <div className="animate-in fade-in slide-in-from-left-2 duration-200">
                            <div className="flex justify-between items-center mb-3">
                              <h4 className="text-sm font-bold text-white">Select QR Logo:</h4>
                              <button onClick={() => setCustomizingId(null)} className="text-zinc-500 hover:text-white"><SafeIcon icon={FiX} /></button>
                            </div>
                            <div className="grid grid-cols-4 gap-2">
                              <button
                                onClick={() => setStyleForCard(tag.id, null)}
                                className={`aspect-square rounded-lg border flex items-center justify-center transition-all ${!currentStyle ? 'bg-white border-white text-black' : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-500'}`}
                                title="No Icon"
                              >
                                <span className="text-[10px] font-bold">NONE</span>
                              </button>
                              {BRAND_ICONS.map(brand => (
                                <button
                                  key={brand.id}
                                  onClick={() => setStyleForCard(tag.id, brand.id)}
                                  className={`aspect-square rounded-lg border flex items-center justify-center transition-all ${currentStyle === brand.id ? 'bg-white border-white' : 'bg-zinc-800 border-zinc-700 hover:border-zinc-500'}`}
                                  title={brand.label}
                                >
                                  {brand.isImage ? (
                                    <img src={brand.icon} alt={brand.label} className="w-4 h-4 object-contain" />
                                  ) : (
                                    <brand.icon size={16} color={currentStyle === brand.id ? brand.color : '#9ca3af'} />
                                  )}
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex justify-between items-start mb-2">
                              <h3 className="font-bold text-lg truncate flex items-center gap-2">
                                <span className="text-zinc-500 font-normal text-sm">ID:</span> {tag.tag_code}
                              </h3>
                              <button
                                onClick={() => handleDelete(tag.id)}
                                disabled={isDeleting}
                                className="text-zinc-600 hover:text-red-500 transition-colors p-1.5 hover:bg-red-500/10 rounded-lg disabled:opacity-50"
                                title="Delete Tag"
                              >
                                {isDeleting ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiTrash2} />}
                              </button>
                            </div>
                            <div className="space-y-3">
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-zinc-500 w-20">Edit Code:</span>
                                <span className="text-red-400 font-mono font-bold bg-red-500/10 px-2 py-0.5 rounded border border-red-500/20 select-all">
                                  {tag.owner_phone || 'N/A'}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-zinc-500 w-20">Status:</span>
                                <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider ${isActive ? "bg-green-500/10 text-green-500 border border-green-500/20" : "bg-amber-500/10 text-amber-500 border border-amber-500/20"}`}>
                                  {isActive ? "Active" : "New"}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-zinc-500 w-20">Type:</span>
                                <span className="text-white capitalize">{tag.link_type || 'Direct'}</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-zinc-500 w-20">Scans:</span>
                                <span className="text-white flex items-center gap-1"><SafeIcon icon={FiEye} className="text-zinc-500"/> {tag.scan_count || 0}</span>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Action Footer */}
                  <div className="bg-zinc-950 px-5 py-3 border-t border-zinc-800 flex justify-between items-center gap-2">
                    <button
                      onClick={() => copyToClipboard(scanUrl, tag.id)}
                      className="text-xs font-bold text-zinc-400 hover:text-white flex items-center gap-1.5 transition-colors py-1"
                    >
                      <SafeIcon icon={copiedId === tag.id ? FiCheck : FiCopy} className={copiedId === tag.id ? "text-green-500" : ""} />
                      {copiedId === tag.id ? 'Copied Link' : 'Copy Scan Link'}
                    </button>
                    {isActive ? (
                      <span className="text-xs font-bold text-green-500 flex items-center gap-1.5 py-1">
                        Live <SafeIcon icon={FiCheck} />
                      </span>
                    ) : (
                      <span className="text-xs text-zinc-600 italic">Not configured</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminPanel;